// Vault page — protected capital ledger.
//
// Canonical design: UI Refactor/Build/Strikza Vault.html
// Layout: inside AppShell (sidebar + topbar + main content).
//
// Financial foundation: reserve_wallets + reserve_transactions
// (Migration 0100). Two sub-wallets (betting + investment), funded
// by Paystack top-ups and auto-protection from chain wins.
//
// CRITICAL RULE: STRIKZA never debits from the reserve. All
// deposits are user-initiated. All outflows are user-initiated
// withdrawals. The reserve is a tracking ledger, not a spending
// account.

import { type ChangeEvent, useState } from "react";
import { Icon } from "../../components/icons/Icon.tsx";
import {
  useVaultLedger,
  goalColor,
  type VaultWallet,
  type VaultGoalAllocation,
  type VaultLedgerEntry,
} from "../../vault/useVaultLedger.ts";
import { useVaultDeposit } from "../../vault/useVaultDeposit.ts";

function fmtGhc(v: number): string {
  return v.toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/* ── Hero ─────────────────────────────────────────────────────────── */

function VaultHero({
  balance,
  wallet,
  loading,
}: {
  balance: number;
  wallet: VaultWallet;
  loading: boolean;
}): JSX.Element {
  return (
    <div className="vault-hero">
      <div>
        <div className="lbl">Vault balance</div>
        <div className="big">
          <span className="ccy">GH₵</span>
          {loading ? "—" : fmtGhc(balance).split(".")[0]}
        </div>
        <div className="cap">
          What's locked away from your chains. A percentage of every win lands here
          automatically — capital you can't lose to a busted week, ready to withdraw
          when you're ready.
        </div>
      </div>
      <div className="right-stats">
        <div>
          <div className="k">Betting reserve</div>
          <div className="v">GH₵{loading ? "—" : fmtGhc(wallet.bettingBalance)}</div>
        </div>
        <div>
          <div className="k">Investment reserve</div>
          <div className="v">GH₵{loading ? "—" : fmtGhc(wallet.investmentBalance)}</div>
        </div>
        <div>
          <div className="k">Total deposited</div>
          <div className="v">GH₵{loading ? "—" : fmtGhc(wallet.totalDeposited)}</div>
        </div>
        <div>
          <div className="k">Total protected</div>
          <div className="v pos">
            {loading ? "—" : `+GH₵${fmtGhc(wallet.totalProtected)}`}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Allocation bar ───────────────────────────────────────────────── */

function AllocationBar({
  goals,
  balance,
  unallocated,
}: {
  goals: VaultGoalAllocation[];
  balance: number;
  unallocated: number;
}): JSX.Element | null {
  if (balance === 0 && goals.length === 0) return null;
  const buckets = goals.length + (unallocated > 0 ? 1 : 0);
  const unallocPct = balance > 0 ? (unallocated / balance) * 100 : 0;

  return (
    <div className="alloc-bar">
      <h3>
        Allocation by goal
        <span className="right">
          GH₵{fmtGhc(balance)} across {buckets} bucket{buckets !== 1 ? "s" : ""}
        </span>
      </h3>
      <div className="alloc-track">
        {goals
          .filter((g) => g.pct > 0)
          .map((g, i) => (
            <div
              key={g.id}
              className="seg"
              style={{ width: `${String(g.pct)}%`, background: goalColor(i) }}
              title={`${g.title} — ${g.pct.toFixed(1)}%`}
            />
          ))}
        {unallocPct > 0 && (
          <div
            className="seg unallocated"
            style={{ width: `${String(unallocPct)}%` }}
            title={`Unallocated — ${unallocPct.toFixed(1)}%`}
          />
        )}
      </div>
      <div
        className="alloc-legend"
        style={{ gridTemplateColumns: `repeat(${String(Math.min(buckets, 4))}, 1fr)` }}
      >
        {goals.map((g, i) => (
          <div className="alloc-leg" key={g.id}>
            <span className="swatch" style={{ background: goalColor(i) }} />
            <div>
              <div className="nm">{g.title}</div>
              <div className="amt">GH₵{fmtGhc(g.protectedAmount)}</div>
              <div className="pct">{g.pct.toFixed(1)}% of vault</div>
            </div>
          </div>
        ))}
        {unallocated > 0 && (
          <div className="alloc-leg">
            <span className="swatch unallocated" />
            <div>
              <div className="nm">Unallocated</div>
              <div className="amt">GH₵{fmtGhc(unallocated)}</div>
              <div className="pct">{unallocPct.toFixed(1)}% of vault</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Ledger ────────────────────────────────────────────────────────── */

function Ledger({ entries }: { entries: VaultLedgerEntry[] }): JSX.Element {
  const [filter, setFilter] = useState("all");
  const filters = [
    { id: "all", label: "All" },
    { id: "in", label: "Inflows" },
    { id: "out", label: "Withdrawals" },
  ];

  const rows = entries.filter((r) => {
    if (filter === "all") return true;
    return r.direction === filter;
  });

  return (
    <div className="ledger">
      <div className="lh">
        <h3>Ledger</h3>
        <span className="meta">
          {rows.length} {rows.length === 1 ? "entry" : "entries"}
        </span>
        <div className="filter-tabs">
          {filters.map((f) => (
            <button
              key={f.id}
              className={filter === f.id ? "on" : ""}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: "32px 20px", color: "var(--text-tertiary)", fontSize: 13 }}>
          No ledger entries yet. Top up your reserve or let chain wins auto-protect
          capital here.
        </div>
      ) : (
        <>
          <div className="ledger-thead">
            <span />
            <span>Movement</span>
            <span>Wallet</span>
            <span>Date</span>
            <span>Ref</span>
            <span style={{ textAlign: "right" }}>Amount</span>
          </div>
          {rows.map((r) => (
            <div className={`ledger-row ${r.direction}`} key={r.id}>
              <span className="ic">
                <Icon name={r.direction === "in" ? "check" : "arrow"} size={13} />
              </span>
              <span className="what">
                {r.label}
                <div className="sub">{r.detail}</div>
              </span>
              <span className="from">
                <span className="gtag">
                  <span
                    className="swatch"
                    style={{
                      background:
                        r.walletType === "betting"
                          ? "var(--accent)"
                          : r.walletType === "investment"
                            ? "#6aa9e9"
                            : "#4ec9a0",
                    }}
                  />
                  {r.walletType === "betting"
                    ? "Betting"
                    : r.walletType === "investment"
                      ? "Investment"
                      : "Protection"}
                </span>
              </span>
              <span className="when">{r.when}</span>
              <span className="ref">{r.ref}</span>
              <span className="amt">
                {r.direction === "in" ? "+" : "-"}GH₵{fmtGhc(r.amount)}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

/* ── Deposit widget ──────────────────────────────────────────────── */

const DEPOSIT_CHIPS = [50, 100, 200, 500];
type DepositWallet = "betting" | "investment";

function DepositWidget({
  onDeposit,
  loading,
  error,
  onClearError,
}: {
  onDeposit: (wallet: DepositWallet, amount: number) => void;
  loading: boolean;
  error: string | null;
  onClearError: () => void;
}): JSX.Element {
  const [amount, setAmount] = useState(0);
  const [wallet, setWallet] = useState<DepositWallet>("betting");

  function handleAmountChange(e: ChangeEvent<HTMLInputElement>): void {
    const raw = e.target.value.replace(/,/g, "");
    const parsed = parseInt(raw, 10);
    setAmount(Number.isNaN(parsed) ? 0 : parsed);
    if (error) onClearError();
  }

  return (
    <div className="vside-card">
      <div className="vh">
        <Icon name="arrowUp" size={14} />
        <h3>Top Up Reserve</h3>
      </div>
      <div className="vb">
        <div className="withdraw-widget">
          {/* Wallet picker */}
          <div>
            <div className="field-label">Deposit to</div>
            <div className="dest-list" style={{ marginTop: 6 }}>
              <div
                className={`dest${wallet === "betting" ? " selected" : ""}`}
                onClick={() => setWallet("betting")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setWallet("betting"); }}
              >
                <div className="logo momo" style={{ background: "var(--accent)", color: "#000" }}>B</div>
                <div>
                  <div className="nm">Betting Reserve</div>
                  <div className="acc">For ROLLOVER chain staking</div>
                </div>
                <div className="check"><Icon name="check" size={14} /></div>
              </div>
              <div
                className={`dest${wallet === "investment" ? " selected" : ""}`}
                onClick={() => setWallet("investment")}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") setWallet("investment"); }}
              >
                <div className="logo bank" style={{ background: "#6aa9e9", color: "#fff" }}>I</div>
                <div>
                  <div className="nm">Investment Reserve</div>
                  <div className="acc">For Market Intelligence positions</div>
                </div>
                <div className="check"><Icon name="check" size={14} /></div>
              </div>
            </div>
          </div>

          {/* Amount input */}
          <div>
            <div className="field-label">Amount</div>
            <div className="withdraw-amount">
              <span className="ccy">GH₵</span>
              <input
                className="num"
                type="text"
                inputMode="numeric"
                value={amount === 0 ? "" : amount.toLocaleString()}
                onChange={handleAmountChange}
                placeholder="0"
              />
            </div>
            <div className="amount-chips">
              {DEPOSIT_CHIPS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`amount-chip${amount === v ? " on" : ""}`}
                  onClick={() => { setAmount(v); if (error) onClearError(); }}
                >
                  GH₵{v.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div style={{ fontSize: 12, color: "var(--neg, #e53e3e)", padding: "4px 0" }}>
              {error}
            </div>
          )}

          {/* Deposit button */}
          <button
            className="btn primary"
            type="button"
            disabled={loading || amount < 10}
            onClick={() => onDeposit(wallet, amount)}
            style={{ width: "100%", justifyContent: "center" }}
          >
            {loading ? (
              <span className="send-spinner" />
            ) : (
              <>
                Top up {wallet === "betting" ? "Betting" : "Investment"} Reserve{" "}
                <Icon name="arrowUp" size={13} />
              </>
            )}
          </button>

          <div style={{ fontSize: 11, color: "var(--text-tertiary)", textAlign: "center", marginTop: -4 }}>
            Powered by Paystack · MTN MoMo, Visa, Mastercard accepted
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Withdraw widget ─────────────────────────────────────────────── */

const AMOUNT_CHIPS = [200, 500, 1000, 2500];

type DestId = "momo" | "bank" | "usdc";

interface Destination {
  id: DestId;
  logoClass: string;
  logoText: string;
  name: string;
  account: string;
}

const DESTINATIONS: Destination[] = [
  { id: "momo", logoClass: "momo", logoText: "MTN", name: "MTN MoMo", account: "024-XXX-9821 · Kwame A." },
  { id: "bank", logoClass: "bank", logoText: "SB", name: "Stanbic Bank · Cedi", account: "0140-XXXX-3201" },
  { id: "usdc", logoClass: "usdc", logoText: "$", name: "USDC · Base network", account: "0x9a4…f12c · GH₵5 fee" },
];

function WithdrawWidget({ balance }: { balance: number }): JSX.Element {
  const [amount, setAmount] = useState(0);
  const [dest, setDest] = useState<DestId>("momo");
  const fee = dest === "usdc" ? 5 : 0;
  const receive = Math.max(0, amount - fee);
  const canWithdraw = amount > 0 && amount <= balance;

  function handleAmountChange(e: ChangeEvent<HTMLInputElement>): void {
    const raw = e.target.value.replace(/,/g, "");
    const parsed = parseInt(raw, 10);
    setAmount(Number.isNaN(parsed) ? 0 : parsed);
  }

  return (
    <div className="vside-card">
      <div className="vh">
        <Icon name="vault" size={14} />
        <h3>Withdraw</h3>
        <span className="right">Available · GH₵{fmtGhc(balance)}</span>
      </div>
      <div className="vb">
        <div className="withdraw-widget">
          {/* Amount input */}
          <div>
            <div className="field-label">Amount</div>
            <div className="withdraw-amount">
              <span className="ccy">GH₵</span>
              <input
                className="num"
                type="text"
                inputMode="numeric"
                value={amount === 0 ? "" : amount.toLocaleString()}
                onChange={handleAmountChange}
                placeholder="0"
              />
              <button
                className="max-btn"
                type="button"
                onClick={() => setAmount(Math.floor(balance))}
              >
                Max
              </button>
            </div>
            <div className="amount-chips">
              {AMOUNT_CHIPS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`amount-chip${amount === v ? " on" : ""}`}
                  onClick={() => setAmount(v)}
                >
                  GH₵{v.toLocaleString()}
                </button>
              ))}
            </div>
          </div>

          {/* Destination picker */}
          <div>
            <div className="field-label">To</div>
            <div className="dest-list" style={{ marginTop: 6 }}>
              {DESTINATIONS.map((d) => (
                <div
                  key={d.id}
                  className={`dest${dest === d.id ? " selected" : ""}`}
                  onClick={() => setDest(d.id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") setDest(d.id);
                  }}
                >
                  <div className={`logo ${d.logoClass}`}>{d.logoText}</div>
                  <div>
                    <div className="nm">{d.name}</div>
                    <div className="acc">{d.account}</div>
                  </div>
                  <div className="check">
                    <Icon name="check" size={14} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Fee summary */}
          <div className="withdraw-summary">
            <div className="row">
              <span>Withdraw amount</span>
              <span className="v">GH₵{fmtGhc(amount)}</span>
            </div>
            <div className="row">
              <span>Network fee</span>
              <span className="v">GH₵{fmtGhc(fee)}</span>
            </div>
            <div className="row total">
              <span>You receive</span>
              <span className="v">GH₵{fmtGhc(receive)}</span>
            </div>
          </div>

          {/* Withdraw button — disabled until withdrawals are live */}
          <button
            className="btn primary"
            type="button"
            disabled={!canWithdraw}
            style={{ width: "100%", justifyContent: "center", opacity: 0.5, cursor: "not-allowed" }}
            title="Withdrawals are not live yet"
          >
            Withdraw to{" "}
            {dest === "momo" ? "MTN MoMo" : dest === "bank" ? "Stanbic Bank" : "USDC"}{" "}
            <Icon name="arrow" size={13} />
          </button>

          <div style={{ fontSize: 11, color: "var(--text-tertiary)", textAlign: "center", marginTop: -4 }}>
            Withdrawals are not live yet — MoMo, bank, and USDC coming soon.
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Goal protection ──────────────────────────────────────────────── */

function GoalProtection({ goals }: { goals: VaultGoalAllocation[] }): JSX.Element {
  const active = goals.filter(
    (g) => g.status !== "cancelled" && g.status !== "abandoned",
  );

  return (
    <div className="vside-card">
      <div className="vh">
        <Icon name="sika" size={14} />
        <h3>Protected by goal</h3>
        <span className="right">{active.length} active</span>
      </div>
      <div className="vb">
        {active.length === 0 ? (
          <div style={{ color: "var(--text-tertiary)", fontSize: 13, lineHeight: 1.55 }}>
            No goals with protection yet. Create a goal in SIKA and link it to a
            ROLLOVER chain — wins will auto-allocate protection here.
          </div>
        ) : (
          <div className="gp-list">
            {active.map((g, i) => {
              const pct =
                g.targetAmount > 0
                  ? (g.protectedAmount / g.targetAmount) * 100
                  : 0;
              return (
                <div className="gp" key={g.id}>
                  <div className="row1">
                    <span
                      className="swatch"
                      style={{ background: goalColor(i) }}
                    />
                    <span className="nm">{g.title}</span>
                    <span className="pct">{pct.toFixed(1)}%</span>
                  </div>
                  <div className="bar">
                    <div
                      className="fill"
                      style={{
                        width: `${String(Math.min(pct, 100))}%`,
                        background: goalColor(i),
                      }}
                    />
                  </div>
                  <div className="meta">
                    <span>
                      <strong>GH₵{fmtGhc(g.protectedAmount)}</strong> of GH₵
                      {fmtGhc(g.targetAmount)}
                    </span>
                    <span>{g.status}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Rules ─────────────────────────────────────────────────────────── */

const VAULT_RULES = [
  {
    lab: "A percentage of every win is auto-protected",
    det: "When a slip resolves as won, STRIKZA moves the protected portion to your vault and tags it to the funding goal.",
  },
  {
    lab: "Vault balance can't be staked",
    det: "Even if a chain busts, vault funds stay untouched. There's no setting that overrides this.",
  },
  {
    lab: "STRIKZA never deducts from your vault",
    det: "All deposits are voluntary — Paystack top-ups or auto-protection from wins. All withdrawals are initiated by you. STRIKZA never pulls from your reserve.",
  },
  {
    lab: "Withdraw any time, no waiting period",
    det: "When withdrawals go live: MoMo arrives in seconds, bank wires settle same day, USDC settles in under a minute on Base.",
  },
  {
    lab: "Goal payouts flow through here",
    det: "When a SIKA goal is achieved, the full amount lands in the vault first — then routes to your withdrawal destination.",
  },
];

function VaultRulesCard(): JSX.Element {
  return (
    <div className="vside-card">
      <div className="vh">
        <Icon name="info" size={14} />
        <h3>How the vault works</h3>
      </div>
      <div className="vb">
        <div className="rule-list">
          {VAULT_RULES.map((r, i) => (
            <div className="vrule" key={i}>
              <div className="ic">
                <Icon name="check" size={14} />
              </div>
              <div>
                <div className="lab">{r.lab}</div>
                <div className="det">{r.det}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Page ──────────────────────────────────────────────────────────── */

export function VaultHomePage(): JSX.Element {
  const vault = useVaultLedger();
  const deposit = useVaultDeposit(() => void vault.refetch?.());

  return (
    <>
      <div className="vault-head">
        <div>
          <h1>Vault</h1>
          <div className="sub">
            Protected capital · auto-funded by every winning slip
          </div>
        </div>
        <div className="actions">
          <button className="btn ghost" type="button">
            <Icon name="settings" size={13} /> Vault rules
          </button>
          <button className="btn ghost" type="button">
            Statements
          </button>
          <button
            className="btn primary"
            type="button"
            onClick={() => {
              // Scroll to deposit widget
              document.querySelector(".vside-card")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            <Icon name="arrowUp" size={13} /> Top Up
          </button>
          <button className="btn ghost" type="button" disabled style={{ opacity: 0.5 }}>
            Withdraw <Icon name="arrow" size={13} />
          </button>
        </div>
      </div>

      <VaultHero
        balance={vault.balance}
        wallet={vault.wallet}
        loading={vault.loading}
      />

      <AllocationBar
        goals={vault.goals}
        balance={vault.balance}
        unallocated={vault.unallocated}
      />

      <div className="vault-grid">
        <Ledger entries={vault.entries} />
        <div className="vault-side">
          <DepositWidget
            onDeposit={(w, a) => void deposit.initDeposit(w, a)}
            loading={deposit.loading}
            error={deposit.error}
            onClearError={deposit.clearError}
          />
          <WithdrawWidget balance={vault.balance} />
          <GoalProtection goals={vault.goals} />
          <VaultRulesCard />
        </div>
      </div>
    </>
  );
}
